import { Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import type { PersonaFieldProps } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/personaFieldProps';

export const DefaultLanguageField = ({
  field,
  data,
  control,
  readonly,
  setValue,
  watch,
}: Omit<PersonaFieldProps<'pf_default_language'>, 'data'> & {
  // Stored as the language key; the option object is handled too.
  data?: string | { value?: string };
}) => {
  const fieldValue = watch(field.key);
  const storedKey = typeof data === 'string' ? data : data?.value;
  const [selectValue, setSelectValue] = useState({
    key: '',
    value: '',
  });
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (field) {
      setOptions(field.options);
    } else {
      setOptions([]);
    }
  }, [field]);

  // The stored language, else the template default; null when there is neither.
  const findInitialOption = () => {
    if (data) {
      return { option: options.find((option) => option.key === storedKey?.toLowerCase()) };
    }
    if (field?.default_value) {
      return { option: options.find((option) => option.key === field?.default_value.key) };
    }
    return null;
  };

  const applyOption = (option?: { key: string; value: string }) => {
    setValue('pf_default_language', option || {});
    setSelectValue(option || { key: '', value: '' });
  };

  useEffect(() => {
    if (!options.length) return;

    if (readonly || !fieldValue?.value) {
      const initial = findInitialOption();

      if (initial) {
        applyOption(initial.option);
      }

      return;
    }

    const key = typeof fieldValue === 'string' ? fieldValue : fieldValue.key;
    applyOption(options.find((option) => option.key === key));
  }, [options, data, readonly]);

  return field && options.length ? (
    <div className={'flex flex-1 flex-col gap-[5px]'}>
      <div>
        <p className={'text-ebony_opacity_30 text-xs font-medium'}>Default language</p>
      </div>
      <div>
        <Controller
          control={control}
          name="pf_default_language"
          render={() => (
            <Select
              getOptionLabel={(option) => option.value}
              getOptionValue={(option) => option.key}
              isDisabled={readonly}
              onChange={(value) => {
                setValue('pf_default_language', value);
                setSelectValue(value);
              }}
              defaultValue={selectValue}
              value={selectValue}
              name="pf_default_language"
              placeholder={'Choose Language'}
              options={options}
              classNamePrefix="select"
              styles={{
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: '#B8C1C6',
                }),
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  boxShadow: 'none',
                  borderColor: state.isFocused ? '#a498ff80!important' : '#E3E6E8',
                  fontFamily: 'Bai Jamjuree',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: state.isDisabled ? '#fff' : baseStyles.background,
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  background: state.isSelected || state.isFocused ? '#a498ff1a' : baseStyles.color,
                  color: state.isSelected ? 'rgba(5, 5, 13);' : 'rgba(5, 5, 13, 0.50);',
                  fontFamily: 'Bai Jamjuree',
                  fontSize: '14px',
                  fontWeight: '500',
                }),
              }}
            />
          )}
        />
      </div>
    </div>
  ) : null;
};
